// @ts-nocheck
import { useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Users, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { FormSelect } from '@/components/shared/FormSelect';
import { FormInput } from '@/components/shared/FormInput';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { AddMemberForm } from './AddMemberForm';
import { MemberList } from '../CrewMemberList';
import { DialogShell } from '@/components/shared/DialogShell';

import { useDialogManager } from '@/hooks/useDialogManager';
import {
  useUniqueCrew,
  useUpdateCrew,
  useDeleteCrewMember,
  useCreateCrewMember,
  useCrewMembers,
} from '@/hooks/api/useCrews';
import { useUsers } from '@/hooks/api/useUsers';
import { useSkills } from '@/hooks/api/useSkills';
import { UserRole } from '@/types/enums';
import { CrewMember, User } from '@/types';

export const EditCrewForm = ({ id }: { id: string }) => {
  const { closeDialog } = useDialogManager();
  const { crew, isLoading, isError } = useUniqueCrew(id);
  const { crewMembers } = useCrewMembers(id);
  const { updateCrew, isUpdating } = useUpdateCrew();
  const { deleteCrewMember, isDeleting } = useDeleteCrewMember(id);
  const { createCrewMember, isCreating: isAdding } = useCreateCrewMember(id);
  const { data: skills, isLoading: skillsLoading } = useSkills();
  const { users } = useUsers({ role: UserRole.CREW });

  const [memberToRemove, setMemberToRemove] = useState<CrewMember | null>(null);
  const [memberToAdd, setMemberToAdd] = useState<User | null>(null);

  const methods = useForm<{ name: string; skills: string[] }>();
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (crew)
      reset({
        name: crew.name,
        skills: crew.skills?.map((s) => s.skillId) ?? [],
      });
  }, [crew, reset]);

  const onSubmit = (data: any) => {
    updateCrew(
      { crewId: crew!.id, name: data.name, skillIds: data.skills },
      {
        onSuccess: () => {
          toast.success(`"${data.name}" updated.`);
          closeDialog();
        },
        onError: (e) =>
          toast.error('Could not update crew.', { description: e.message }),
      },
    );
  };

  const availableMembers = useMemo(() => {
    const ids = new Set(crewMembers.map((m) => m.userId));
    return users?.filter((u) => !ids.has(u.id)) ?? [];
  }, [users, crewMembers]);

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
        Loading...
      </div>
    );
  if (isError || !crew)
    return (
      <div className="flex items-center justify-center h-40 text-sm text-destructive">
        Failed to load crew.
      </div>
    );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0">
        <DialogShell
          icon={<Users className="w-4 h-4 text-primary" />}
          title={`Edit Crew — ${crew.name}`}
          description="Update crew details and manage team members."
          onCancel={closeDialog}
          submitText="Save Changes"
          isSubmitting={isUpdating}
          isDirty={isDirty}
          submitDisabled={!isDirty || isAdding || isDeleting}
        >
          {/* ── Crew details ── */}
          <fieldset className="space-y-4">
            <FormInput
              name="name"
              label="Crew Name"
              rules={{ required: 'Crew name is required' }}
            />
            <FormSelect
              name="skills"
              label="Skills"
              multiple
              disabled={skillsLoading}
              placeholder={skillsLoading ? 'Loading…' : 'Select skills'}
              options={
                skills?.map((s) => ({ value: s.id, label: s.name })) ?? []
              }
            />
          </fieldset>

          {/* ── Members ── */}
          <div className="space-y-2.5 pt-1">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2 text-sm font-semibold">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                Members
                <span className="text-xs font-normal text-muted-foreground">
                  ({crewMembers.length})
                </span>
              </Label>
              <AddMemberForm
                members={availableMembers}
                onAddMember={(userId) => {
                  const user = users?.find((u) => u.id === userId);
                  if (user) setMemberToAdd(user);
                }}
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                  >
                    <UserPlus className="h-3 w-3" /> Add
                  </Button>
                }
              />
            </div>

            <MemberList
              members={crewMembers.map((m) => ({
                id: m.userId,
                name: m.user.name,
                email: m.user.email,
              }))}
              onRemove={(userId) => {
                const member = crewMembers.find((m) => m.userId === userId);
                if (member) setMemberToRemove(member);
              }}
              isRemoving={isDeleting}
              emptyMessage="No members assigned. Add someone above."
            />
          </div>
        </DialogShell>
      </form>

      {/* Confirm remove */}
      <ConfirmDialog
        open={!!memberToRemove}
        onOpenChange={(o) => !o && setMemberToRemove(null)}
        title="Remove Member?"
        description={`Remove ${memberToRemove?.user.name} from this crew?`}
        onConfirm={() => {
          if (!memberToRemove) return;
          deleteCrewMember(
            { userId: memberToRemove.userId, crewId: crew!.id },
            {
              onSuccess: () => {
                toast.success(`${memberToRemove.user.name} removed.`);
                setMemberToRemove(null);
              },
              onError: (e) => {
                toast.error('Failed to remove.', { description: e.message });
                setMemberToRemove(null);
              },
            },
          );
        }}
      />

      {/* Confirm add */}
      <ConfirmDialog
        open={!!memberToAdd}
        onOpenChange={(o) => !o && setMemberToAdd(null)}
        title="Add Member?"
        description={`Add ${memberToAdd?.name} to this crew?`}
        variant="default"
        confirmText="Add Member"
        onConfirm={() => {
          if (!memberToAdd) return;
          createCrewMember(
            { userId: memberToAdd.id, crewId: crew!.id },
            {
              onSuccess: () => {
                toast.success(`${memberToAdd.name} added.`);
                setMemberToAdd(null);
              },
              onError: (e) => {
                toast.error('Failed to add.', { description: e.message });
                setMemberToAdd(null);
              },
            },
          );
        }}
      />
    </FormProvider>
  );
};
